import type {
    CallOutcome,
    ClinicData,
    FollowUp,
    Gender,
    Medicine,
    MessageKind,
    Patient,
    Prescription,
    ReminderLog,
    Visit
} from '@/lib/clinic/types'

import { addDays, parseISODate } from '@/lib/clinic/dates'
import { createDefaultSettings, createDefaultTreatments } from '@/lib/clinic/defaults'
import { fillTemplate } from '@/lib/clinic/messaging'
import { patientCode, prescriptionCode } from '@/lib/clinic/format'

type Rx = Omit<Medicine, 'id'>

interface Step {
    complaint: string
    diagnosis: string
    treatment: string
    notes: string
    advice?: string
    instructions?: string
    rx?: Rx[]
    next?: { days: number, reason: string }
}

type StoryKey =
    | 'rct'
    | 'scaling'
    | 'wisdom'
    | 'extraction'
    | 'filling'
    | 'sensitivity'
    | 'denture'
    | 'bridge'
    | 'gum'
    | 'whitening'
    | 'child'
    | 'checkup'
    | 'abscess'

interface ReminderSeed {
    daysAgo: number
    channel: 'whatsapp' | 'call'
    template?: MessageKind
    outcome?: CallOutcome
    note?: string
}

interface Scenario {
    name: string
    gender: Gender
    age: number
    place: string
    phone: string
    address?: string
    medicalHistory?: string
    allergies?: string
    notes?: string
    story: StoryKey
    /** Tooth wording used in the clinical notes. */
    tooth?: string
    /** Tooth numbers stored on the visit, defaults to `tooth`. */
    teeth?: string
    steps: number
    lastVisitAgo: number
    followUp?: {
        dueIn: number
        time?: string
        confirmed?: boolean
        status?: 'pending' | 'cancelled'
        cancelledAgo?: number
        note?: string
    }
    reminders?: ReminderSeed[]
    prior?: Array<{ story: StoryKey, step: number, daysAgo: number, tooth?: string }>
}

const AMOXICILLIN: Rx = { name: "Amoxicillin 500 mg", dosage: "1 capsule", frequency: "1-1-1", duration: "5 days", instructions: "After food" }
const AMOX_CLAV: Rx = { name: "Amoxicillin + Clavulanic acid 625 mg", dosage: "1 tablet", frequency: "1-0-1", duration: "5 days", instructions: "After food" }
const CLINDAMYCIN: Rx = { name: "Clindamycin 300 mg", dosage: "1 capsule", frequency: "1-1-1", duration: "5 days", instructions: "After food" }
const METRONIDAZOLE: Rx = { name: "Metronidazole 400 mg", dosage: "1 tablet", frequency: "1-1-1", duration: "5 days", instructions: "After food. Avoid alcohol" }
const IBU_PARA: Rx = { name: "Ibuprofen 400 mg + Paracetamol 325 mg", dosage: "1 tablet", frequency: "1-0-1", duration: "3 days", instructions: "After food" }
const ACECLO_PARA: Rx = { name: "Aceclofenac 100 mg + Paracetamol 325 mg", dosage: "1 tablet", frequency: "1-0-1", duration: "3 days", instructions: "After food" }
const PARACETAMOL: Rx = { name: "Paracetamol 650 mg", dosage: "1 tablet", frequency: "1-0-1", duration: "3 days", instructions: "After food" }
const PARACETAMOL_SOS: Rx = { name: "Paracetamol 650 mg", dosage: "1 tablet", frequency: "SOS", duration: "3 days", instructions: "If pain, after food" }
const PANTOPRAZOLE: Rx = { name: "Pantoprazole 40 mg", dosage: "1 tablet", frequency: "1-0-0", duration: "5 days", instructions: "Before breakfast" }
const CHLORHEXIDINE: Rx = { name: "Chlorhexidine 0.2% mouthwash", dosage: "10 ml", frequency: "1-0-1", duration: "14 days", instructions: "Rinse for 30 seconds, do not swallow" }
const DESENSITISING: Rx = { name: "Potassium nitrate desensitising toothpaste", dosage: "Pea-sized amount", frequency: "1-0-1", duration: "1 month", instructions: "Brush gently with a soft brush" }
const GUM_GEL: Rx = { name: "Metronidazole + Chlorhexidine gum gel", dosage: "Apply a thin layer", frequency: "1-0-1", duration: "7 days", instructions: "Apply on the gums after brushing" }
const PARA_SYRUP: Rx = { name: "Paracetamol syrup 250 mg/5 ml", dosage: "5 ml", frequency: "SOS", duration: "3 days", instructions: "If pain, up to 3 doses a day" }
const LIDOCAINE_GEL: Rx = { name: "Lidocaine 2% oral gel", dosage: "Apply a thin layer", frequency: "1-1-1", duration: "5 days", instructions: "Apply on sore spots" }

const RCT_INSTRUCTIONS = [
    "Avoid chewing hard food on the treated side until the crown is fixed.",
    "Mild pain for 2 to 3 days is normal. Take the medicines after food.",
    "Call the clinic if the swelling increases."
].join('\n')

const EXTRACTION_INSTRUCTIONS = [
    "Bite firmly on the gauze for 45 minutes.",
    "Do not spit, rinse or use a straw for 24 hours.",
    "Have soft, cold food today and avoid hot drinks.",
    "Avoid smoking and alcohol for at least 3 days."
].join('\n')

const GUM_INSTRUCTIONS = [
    "Brush twice daily with a soft toothbrush.",
    "Use the mouthwash 30 minutes after brushing.",
    "Mild sensitivity after cleaning settles in a few days."
].join('\n')

const DENTURE_INSTRUCTIONS = [
    "Start with soft food cut into small pieces.",
    "Remove and rinse the dentures after every meal.",
    "Keep the dentures in a bowl of water overnight."
].join('\n')

const STORIES: Record<StoryKey, Step[]> = {
    rct: [
        {
            complaint: "Tooth Pain",
            diagnosis: "Symptomatic irreversible pulpitis in {t}",
            treatment: "Root Canal Treatment: access opening and pulp extirpation",
            notes: "Severe pain on chewing for 4 days. Tender on percussion. Temporary filling placed.",
            advice: "Root canal treatment started. Three sittings planned, followed by a crown.",
            instructions: RCT_INSTRUCTIONS,
            rx: [AMOXICILLIN, IBU_PARA, PANTOPRAZOLE],
            next: { days: 7, reason: "Root canal: second sitting" }
        },
        {
            complaint: "Root canal follow-up",
            diagnosis: "Irreversible pulpitis in {t}, under treatment",
            treatment: "Root Canal Treatment: cleaning, shaping and medicament",
            notes: "Canals cleaned and shaped. Calcium hydroxide dressing placed. Pain reduced.",
            next: { days: 7, reason: "Root canal: final sitting" }
        },
        {
            complaint: "Root canal follow-up",
            diagnosis: "Irreversible pulpitis in {t}, under treatment",
            treatment: "Root Canal Treatment: obturation and post-endodontic filling",
            notes: "Obturation completed. Patient comfortable. Crown advised to protect the tooth.",
            advice: "Root canal completed. A crown is recommended within 2 weeks.",
            rx: [PARACETAMOL_SOS],
            next: { days: 10, reason: "Crown preparation" }
        },
        {
            complaint: "Crown for treated tooth",
            diagnosis: "Root canal treated {t}",
            treatment: "Dental Crown: tooth preparation and impression",
            notes: "Zirconia crown selected, shade A2. Temporary crown cemented.",
            next: { days: 7, reason: "Crown cementation" }
        },
        {
            complaint: "Crown fitting",
            diagnosis: "Root canal treated {t}",
            treatment: "Dental Crown: cementation",
            notes: "Crown cemented. Bite checked and adjusted.",
            next: { days: 180, reason: "Six-month review" }
        }
    ],
    scaling: [
        {
            complaint: "Bleeding gums",
            diagnosis: "Chronic generalised gingivitis with moderate calculus",
            treatment: "Teeth Cleaning / Scaling: full mouth scaling and polishing",
            notes: "Bleeding while brushing for 2 months. Oral hygiene instructions given.",
            advice: "Brush twice daily with a soft brush and floss once a day.",
            instructions: GUM_INSTRUCTIONS,
            rx: [CHLORHEXIDINE, DESENSITISING],
            next: { days: 180, reason: "Six-month cleaning and checkup" }
        },
        {
            complaint: "Dental Checkup",
            diagnosis: "Gingival health improved",
            treatment: "Teeth Cleaning / Scaling: oral prophylaxis",
            notes: "Gums healthy. Minimal calculus.",
            next: { days: 180, reason: "Six-month cleaning and checkup" }
        }
    ],
    wisdom: [
        {
            complaint: "Pain in lower back tooth",
            diagnosis: "Mesioangular impacted {t} with pericoronitis",
            treatment: "Tooth Extraction: surgical removal of {t}",
            notes: "Surgical extraction under local anaesthesia. 3 sutures placed. Bleeding controlled.",
            advice: "Use an ice pack on the cheek for 10 minutes every hour today.",
            instructions: EXTRACTION_INSTRUCTIONS,
            rx: [AMOX_CLAV, METRONIDAZOLE, ACECLO_PARA, PANTOPRAZOLE],
            next: { days: 7, reason: "Suture removal" }
        },
        {
            complaint: "Suture removal",
            diagnosis: "Healing extraction socket {t}",
            treatment: "Suture removal and wound check",
            notes: "Sutures removed. Healing is satisfactory."
        }
    ],
    extraction: [
        {
            complaint: "Broken tooth",
            diagnosis: "Grossly decayed {t}, not restorable",
            treatment: "Tooth Extraction of {t}",
            notes: "Extraction under local anaesthesia. Post-extraction care explained.",
            instructions: EXTRACTION_INSTRUCTIONS,
            rx: [AMOXICILLIN, PARACETAMOL],
            next: { days: 14, reason: "Review and replacement options" }
        },
        {
            complaint: "Review after extraction",
            diagnosis: "Healed extraction socket {t}",
            treatment: "Dental Checkup: discussed bridge and implant options",
            notes: "Socket healed well. Patient will decide on the replacement."
        }
    ],
    filling: [
        {
            complaint: "Food lodgement",
            diagnosis: "Dental caries in {t}",
            treatment: "Dental Filling: composite restoration on {t}",
            notes: "Caries removed and composite filling placed. Avoid hard food for 24 hours.",
            next: { days: 180, reason: "Six-month checkup" }
        }
    ],
    sensitivity: [
        {
            complaint: "Sensitivity to cold",
            diagnosis: "Dentine hypersensitivity with cervical abrasion on {t}",
            treatment: "Dental Filling: glass ionomer restoration on {t}",
            notes: "Hard brushing habit. Correct brushing technique demonstrated.",
            advice: "Use a soft brush and the desensitising toothpaste twice daily.",
            rx: [DESENSITISING],
            next: { days: 30, reason: "Sensitivity review" }
        },
        {
            complaint: "Sensitivity review",
            diagnosis: "Dentine hypersensitivity, improved",
            treatment: "Dental Checkup: review",
            notes: "Sensitivity reduced. Continue the desensitising toothpaste for another month."
        }
    ],
    denture: [
        {
            complaint: "Missing teeth",
            diagnosis: "Completely edentulous upper and lower arches",
            treatment: "Denture: primary impressions",
            notes: "Complete dentures planned. Procedure and appointments explained.",
            next: { days: 5, reason: "Denture: final impression" }
        },
        {
            complaint: "Denture appointment",
            diagnosis: "Completely edentulous upper and lower arches",
            treatment: "Denture: border moulding and final impression",
            notes: "Final impressions recorded with custom trays.",
            next: { days: 7, reason: "Denture: jaw relation" }
        },
        {
            complaint: "Denture appointment",
            diagnosis: "Completely edentulous upper and lower arches",
            treatment: "Denture: jaw relation recorded",
            notes: "Bite height recorded. Tooth shade and size selected.",
            next: { days: 7, reason: "Denture: try-in" }
        },
        {
            complaint: "Denture try-in",
            diagnosis: "Completely edentulous upper and lower arches",
            treatment: "Denture: wax try-in",
            notes: "Appearance, speech and bite checked. Patient happy with the look.",
            next: { days: 7, reason: "Denture insertion" }
        },
        {
            complaint: "Denture insertion",
            diagnosis: "Completely edentulous upper and lower arches",
            treatment: "Denture: insertion and care instructions",
            notes: "Dentures inserted. Cleaning and eating instructions given.",
            advice: "Remove the dentures at night and keep them in water.",
            instructions: DENTURE_INSTRUCTIONS,
            rx: [LIDOCAINE_GEL],
            next: { days: 3, reason: "Denture review" }
        },
        {
            complaint: "Denture review",
            diagnosis: "Complete denture wearer",
            treatment: "Denture: adjustment of sore spots",
            notes: "Minor sore spots relieved. Patient adapting well.",
            next: { days: 30, reason: "Denture review" }
        }
    ],
    bridge: [
        {
            complaint: "Missing tooth",
            diagnosis: "Missing {t}",
            treatment: "Dental Bridge: abutment preparation and impression",
            notes: "Three-unit bridge planned. Temporary bridge placed.",
            next: { days: 7, reason: "Bridge cementation" }
        },
        {
            complaint: "Bridge fitting",
            diagnosis: "Missing {t}, bridge in place",
            treatment: "Dental Bridge: cementation",
            notes: "Bridge cemented. Floss threader use demonstrated.",
            next: { days: 180, reason: "Six-month review" }
        }
    ],
    gum: [
        {
            complaint: "Loose teeth and swollen gums",
            diagnosis: "Chronic generalised periodontitis",
            treatment: "Gum Treatment: scaling and root planing, upper arch",
            notes: "Deep pockets in the upper molars. Treatment split into two sittings.",
            advice: "Gum treatment in two sittings, followed by a review.",
            instructions: GUM_INSTRUCTIONS,
            rx: [CHLORHEXIDINE, METRONIDAZOLE, GUM_GEL],
            next: { days: 7, reason: "Gum treatment: lower arch" }
        },
        {
            complaint: "Gum treatment follow-up",
            diagnosis: "Chronic generalised periodontitis",
            treatment: "Gum Treatment: scaling and root planing, lower arch",
            notes: "Lower arch completed. Less bleeding than at the last visit.",
            rx: [CHLORHEXIDINE],
            next: { days: 30, reason: "Periodontal review" }
        },
        {
            complaint: "Periodontal review",
            diagnosis: "Chronic periodontitis, improving",
            treatment: "Gum Treatment: review and polishing",
            notes: "Pocket depths reduced. Maintenance every 3 months advised.",
            next: { days: 90, reason: "Periodontal maintenance" }
        }
    ],
    whitening: [
        {
            complaint: "Discoloured teeth",
            diagnosis: "Extrinsic stains",
            treatment: "Teeth Whitening: scaling and in-office whitening session",
            notes: "Shade improved from A3 to A1. Avoid tea, coffee and coloured food for 48 hours.",
            rx: [DESENSITISING],
            next: { days: 14, reason: "Whitening review" }
        },
        {
            complaint: "Whitening review",
            diagnosis: "Post-whitening review",
            treatment: "Dental Checkup: shade check",
            notes: "Shade stable. No sensitivity."
        }
    ],
    child: [
        {
            complaint: "Tooth Pain",
            diagnosis: "Deep caries in {t} with pulp involvement",
            treatment: "Pulpotomy and stainless steel crown on {t}",
            notes: "Child was cooperative. Parent advised to limit sweets and supervise brushing.",
            advice: "Brush twice daily with a pea-sized amount of fluoride toothpaste.",
            rx: [PARA_SYRUP],
            next: { days: 30, reason: "Review of treated tooth" }
        }
    ],
    checkup: [
        {
            complaint: "Dental Checkup",
            diagnosis: "Early caries in {t}",
            treatment: "Dental Checkup: oral examination and fluoride application",
            notes: "Small cavities found. Fillings advised before they grow.",
            next: { days: 7, reason: "Dental filling" }
        },
        {
            complaint: "Dental filling",
            diagnosis: "Dental caries in {t}",
            treatment: "Dental Filling: composite restoration on {t}",
            notes: "Fillings completed. Patient comfortable.",
            next: { days: 180, reason: "Six-month checkup" }
        }
    ],
    abscess: [
        {
            complaint: "Swelling in lower jaw",
            diagnosis: "Acute dentoalveolar abscess in relation to {t}",
            treatment: "Root Canal Treatment: emergency access opening and drainage",
            notes: "Diffuse swelling on the right side. Tooth opened for drainage. Antibiotics started.",
            advice: "Warm salt water rinses every 2 hours. Come back early if the swelling spreads.",
            instructions: RCT_INSTRUCTIONS,
            rx: [AMOX_CLAV, METRONIDAZOLE, IBU_PARA, PANTOPRAZOLE],
            next: { days: 3, reason: "Swelling review and root canal" }
        }
    ]
}

const SCENARIOS: Scenario[] = [
    { name: "Arjun Menon", gender: 'male', age: 34, place: "Kakkanad", phone: "98471 23560", address: "Flat 4B, Skyline Ivy League, Kakkanad, Kochi", story: 'rct', tooth: "36", steps: 1, lastVisitAgo: 0, followUp: { dueIn: 7, time: '10:30' } },
    { name: "Fathima Rahman", gender: 'female', age: 27, place: "Aluva", phone: "97456 31842", story: 'scaling', steps: 1, lastVisitAgo: 0, followUp: { dueIn: 182 } },
    { name: "Joseph Thomas", gender: 'male', age: 22, place: "Thrissur", phone: "94952 77310", notes: "Engineering student. Prefers evening appointments.", story: 'wisdom', tooth: "38", steps: 1, lastVisitAgo: 0, followUp: { dueIn: 7, time: '17:00' } },
    { name: "Ammini Varghese", gender: 'female', age: 68, place: "Ernakulam", phone: "94470 61295", address: "Kunnel House, Chittoor Road, Ernakulam", medicalHistory: "Type 2 diabetes (on Metformin), high blood pressure", notes: "The phone number belongs to her daughter, Sini.", story: 'denture', steps: 4, lastVisitAgo: 0, followUp: { dueIn: 7, time: '11:00' } },
    { name: "Rahul Krishnan", gender: 'male', age: 29, place: "Edappally", phone: "99610 45823", allergies: "Penicillin", story: 'rct', tooth: "46", steps: 1, lastVisitAgo: 7, followUp: { dueIn: 0, time: '10:30', confirmed: true }, reminders: [{ daysAgo: 1, channel: 'whatsapp', template: 'reminder' }, { daysAgo: 1, channel: 'call', outcome: 'confirmed', note: "Will come at 10:30 AM." }] },
    { name: "Lakshmi Pillai", gender: 'female', age: 52, place: "Tripunithura", phone: "98950 12476", address: "Sreevalsam, Hill Palace Road, Tripunithura", medicalHistory: "High blood pressure (on Amlodipine)", story: 'gum', steps: 1, lastVisitAgo: 7, followUp: { dueIn: 0, time: '16:00' }, reminders: [{ daysAgo: 1, channel: 'whatsapp', template: 'reminder' }], prior: [{ story: 'scaling', step: 0, daysAgo: 310 }] },
    { name: "Muhammed Shafi", gender: 'male', age: 45, place: "Perumbavoor", phone: "97440 58631", story: 'bridge', tooth: "46", teeth: "45, 46, 47", steps: 1, lastVisitAgo: 7, followUp: { dueIn: 0, time: '11:30' } },
    { name: "Sneha Varghese", gender: 'female', age: 31, place: "Kalamassery", phone: "90371 22984", story: 'rct', tooth: "26", steps: 2, lastVisitAgo: 6, followUp: { dueIn: 1, time: '10:00' } },
    { name: "Vishnu Prasad", gender: 'male', age: 24, place: "Angamaly", phone: "81291 64037", story: 'wisdom', tooth: "48", steps: 1, lastVisitAgo: 6, followUp: { dueIn: 1 } },
    { name: "Anjali Nair", gender: 'female', age: 26, place: "Vyttila", phone: "96330 78152", story: 'whitening', steps: 1, lastVisitAgo: 13, followUp: { dueIn: 1, time: '17:30' }, reminders: [{ daysAgo: 0, channel: 'whatsapp', template: 'reminder' }] },
    { name: "Abdul Rasheed", gender: 'male', age: 49, place: "Muvattupuzha", phone: "94464 39270", medicalHistory: "Type 2 diabetes", story: 'rct', tooth: "37", steps: 3, lastVisitAgo: 5, followUp: { dueIn: 5 } },
    { name: "Gopalan Nair", gender: 'male', age: 72, place: "Kothamangalam", phone: "94953 08614", address: "Padmalayam, Market Road, Kothamangalam", medicalHistory: "High blood pressure. Takes Aspirin 75 mg daily (blood thinner).", notes: "Hard of hearing. Speak slowly and clearly.", story: 'denture', steps: 2, lastVisitAgo: 3, followUp: { dueIn: 4, time: '12:00' } },
    { name: "Meera Suresh", gender: 'female', age: 38, place: "Palarivattom", phone: "98463 57102", story: 'scaling', steps: 1, lastVisitAgo: 150, followUp: { dueIn: 30 }, prior: [{ story: 'filling', step: 0, daysAgo: 330, tooth: "16" }] },
    { name: "Kiran Das", gender: 'male', age: 19, place: "Kaloor", phone: "77360 91485", story: 'filling', tooth: "16 and 17", teeth: "16, 17", steps: 1, lastVisitAgo: 170, followUp: { dueIn: 10 } },
    { name: "Aadhya Ramesh", gender: 'female', age: 7, place: "Thrikkakara", phone: "99474 26318", notes: "Child patient. Her mother, Divya Ramesh, comes with her.", story: 'child', tooth: "75", steps: 1, lastVisitAgo: 20, followUp: { dueIn: 10, time: '16:30' } },
    { name: "Nithya George", gender: 'female', age: 33, place: "Fort Kochi", phone: "95265 40791", allergies: "Sulfa drugs", story: 'checkup', tooth: "24 and 25", teeth: "24, 25", steps: 1, lastVisitAgo: 2, followUp: { dueIn: 5, time: '12:00', confirmed: true }, reminders: [{ daysAgo: 0, channel: 'whatsapp', template: 'confirmation' }] },
    { name: "Sreejith Kumar", gender: 'male', age: 41, place: "Kadavanthra", phone: "98951 73624", medicalHistory: "Asthma (uses an inhaler when needed)", story: 'rct', tooth: "16", steps: 1, lastVisitAgo: 16, followUp: { dueIn: -9 }, reminders: [{ daysAgo: 7, channel: 'whatsapp', template: 'overdue' }, { daysAgo: 3, channel: 'call', outcome: 'no-answer' }] },
    { name: "Aysha Beevi", gender: 'female', age: 56, place: "Mattancherry", phone: "97471 85203", medicalHistory: "Type 2 diabetes", story: 'gum', steps: 2, lastVisitAgo: 40, followUp: { dueIn: -10 }, reminders: [{ daysAgo: 8, channel: 'whatsapp', template: 'overdue' }] },
    { name: "Deepak Raj", gender: 'male', age: 36, place: "Panampilly Nagar", phone: "90487 31956", story: 'rct', tooth: "47", steps: 4, lastVisitAgo: 12, followUp: { dueIn: -5 }, reminders: [{ daysAgo: 4, channel: 'whatsapp', template: 'reminder' }, { daysAgo: 1, channel: 'call', outcome: 'call-back', note: "Busy at work this week. Will call back to fix a date." }] },
    { name: "Divya Mohan", gender: 'female', age: 30, place: "Chalakudy", phone: "81369 25740", story: 'scaling', steps: 1, lastVisitAgo: 190, followUp: { dueIn: -10 }, prior: [{ story: 'checkup', step: 0, daysAgo: 400, tooth: "36" }] },
    { name: "Ramesh Babu", gender: 'male', age: 58, place: "North Paravur", phone: "94471 60382", medicalHistory: "High blood pressure", story: 'extraction', tooth: "14", steps: 1, lastVisitAgo: 20, followUp: { dueIn: -6 }, reminders: [{ daysAgo: 2, channel: 'call', outcome: 'no-answer' }] },
    { name: "Haris Muhammed", gender: 'male', age: 27, place: "Aluva", phone: "75928 14067", story: 'abscess', tooth: "46", steps: 1, lastVisitAgo: 5, followUp: { dueIn: -2, time: '10:00' }, reminders: [{ daysAgo: 1, channel: 'whatsapp', template: 'overdue' }] },
    { name: "Asha Joseph", gender: 'female', age: 44, place: "Thevara", phone: "98472 90531", medicalHistory: "Hypothyroidism (on Thyroxine)", story: 'wisdom', tooth: "38", steps: 2, lastVisitAgo: 30 },
    { name: "Basil Paul", gender: 'male', age: 39, place: "Kolenchery", phone: "96051 37428", story: 'rct', tooth: "36", steps: 5, lastVisitAgo: 60, followUp: { dueIn: 120 } },
    { name: "Reshma Ali", gender: 'female', age: 28, place: "Kalamassery", phone: "70126 58934", story: 'whitening', steps: 2, lastVisitAgo: 45 },
    { name: "Jaison Jose", gender: 'male', age: 35, place: "Kottayam", phone: "94473 26150", story: 'filling', tooth: "26", steps: 1, lastVisitAgo: 90, followUp: { dueIn: 90, status: 'cancelled', cancelledAgo: 20, note: "Moved to Bengaluru for work. Will continue treatment there." } },
    { name: "Saraswathi Amma", gender: 'female', age: 70, place: "Piravom", phone: "85475 09316", medicalHistory: "High blood pressure, knee arthritis", notes: "Needs help with the stairs. Prefers morning slots.", story: 'denture', steps: 6, lastVisitAgo: 25, followUp: { dueIn: 5, time: '10:00' } },
    { name: "Anu Mathew", gender: 'female', age: 47, place: "Kakkanad", phone: "99952 64873", medicalHistory: "Type 2 diabetes", story: 'gum', steps: 3, lastVisitAgo: 80, followUp: { dueIn: 10 } },
    { name: "Nandana S", gender: 'female', age: 21, place: "Edappally", phone: "62382 41759", story: 'checkup', tooth: "36 and 46", teeth: "36, 46", steps: 2, lastVisitAgo: 14 },
    { name: "Rinshad M", gender: 'male', age: 32, place: "Perumbavoor", phone: "97786 30542", allergies: "Ibuprofen (causes rashes)", story: 'sensitivity', tooth: "13 and 14", teeth: "13, 14", steps: 2, lastVisitAgo: 29 }
]

/** Small deterministic random generator so the sample data looks the same every time it is created. */
const createRandom = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0
        let t = state
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)

        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

const withTooth = (text: string, tooth?: string): string => text.replace(/\{t\}/g, tooth ?? 'the affected tooth')

const adaptForAllergies = (rx: Rx[], allergies?: string): Rx[] => {
    const allergyText = (allergies ?? '').toLowerCase()

    return rx.map(item => {
        if (allergyText.includes('penicillin') && item.name.toLowerCase().includes('amoxicillin')) return CLINDAMYCIN
        if (allergyText.includes('ibuprofen') && /ibuprofen|aceclofenac/i.test(item.name)) return PARACETAMOL

        return item
    })
}

export const createSampleData = (today: string, now: Date = new Date()): ClinicData => {
    const random = createRandom(20261101)
    const settings = createDefaultSettings()
    const visits: Visit[] = []
    const followUps: FollowUp[] = []
    const reminders: ReminderLog[] = []
    const prescriptionDrafts: Array<Omit<Prescription, 'id'>> = []
    const counters = { visit: 0, followUp: 0, reminder: 0, medicine: 0 }
    let todayOrder = 0

    const stamp = (date: string, hour: number, minute: number): string => {
        const value = parseISODate(date)
        value.setHours(hour, minute, 0, 0)

        if (value.getTime() <= now.getTime()) return value.toISOString()

        todayOrder += 1

        const fallback = new Date(now.getTime() - Math.max(2, 90 - todayOrder * 6) * 60000)
        const startOfDay = parseISODate(date)

        return (fallback.getTime() < startOfDay.getTime() ? startOfDay : fallback).toISOString()
    }

    const shiftMinutes = (iso: string, minutes: number): string => {
        const shifted = new Date(new Date(iso).getTime() + minutes * 60000)

        return (shifted.getTime() > now.getTime() ? new Date(iso) : shifted).toISOString()
    }

    const randomSlot = (): { hour: number, minute: number } => {
        const slot = Math.floor(random() * 36)
        const minutes = 9 * 60 + 30 + slot * 15

        return { hour: Math.floor(minutes / 60), minute: minutes % 60 }
    }

    const planned = SCENARIOS.map(scenario => {
        const steps = STORIES[scenario.story].slice(0, scenario.steps)
        const dates: string[] = new Array(steps.length)

        dates[steps.length - 1] = addDays(today, -scenario.lastVisitAgo)

        for (let index = steps.length - 2; index >= 0; index -= 1) {
            const gap = steps[index].next?.days ?? 7
            const late = random() < 0.35 ? 1 + Math.floor(random() * 2) : 0
            dates[index] = addDays(dates[index + 1], -(gap + late))
        }

        const prior = (scenario.prior ?? []).map(item => ({
            step: STORIES[item.story][item.step],
            date: addDays(today, -item.daysAgo),
            tooth: item.tooth
        }))

        const firstDate = [...prior.map(item => item.date), dates[0]].sort()[0]

        return { scenario, steps, dates, prior, firstDate }
    })

    planned.sort((a, b) => a.firstDate.localeCompare(b.firstDate))

    const patients: Patient[] = planned.map((plan, patientIndex) => {
        const { scenario } = plan
        const patientId = patientCode(1001 + patientIndex)
        const firstSlot = randomSlot()
        const registeredAt = stamp(plan.firstDate, firstSlot.hour, firstSlot.minute)

        const addVisit = (step: Step, date: string, tooth: string | undefined, teeth: string | undefined): Visit => {
            const slot = date === plan.firstDate ? firstSlot : randomSlot()
            const createdAt = date === plan.firstDate ? shiftMinutes(registeredAt, 4) : stamp(date, slot.hour, slot.minute)

            counters.visit += 1

            const visit: Visit = {
                id: `vis_${String(counters.visit).padStart(4, '0')}`,
                patientId,
                date,
                complaint: step.complaint,
                diagnosis: withTooth(step.diagnosis, tooth),
                treatment: withTooth(step.treatment, tooth),
                toothNumbers: teeth ?? tooth?.replace(/ and /g, ', ') ?? '',
                notes: step.notes,
                createdAt,
                updatedAt: createdAt
            }

            visits.push(visit)

            if (step.rx?.length) {
                const rxCreatedAt = shiftMinutes(createdAt, 9)

                prescriptionDrafts.push({
                    patientId,
                    visitId: visit.id,
                    date,
                    complaint: step.complaint,
                    diagnosis: visit.diagnosis,
                    advice: step.advice ?? '',
                    medicines: adaptForAllergies(step.rx, scenario.allergies).map(item => {
                        counters.medicine += 1

                        return { ...item, id: `med_${counters.medicine}` }
                    }),
                    instructions: step.instructions ?? settings.prescription.defaultInstructions,
                    notes: '',
                    printNotes: false,
                    createdAt: rxCreatedAt,
                    updatedAt: rxCreatedAt
                })
            }

            return visit
        }

        plan.prior
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .forEach(item => addVisit(item.step, item.date, item.tooth, item.tooth))

        const storyVisits = plan.steps.map((step, index) => addVisit(step, plan.dates[index], scenario.tooth, scenario.teeth))

        storyVisits.forEach((visit, index) => {
            const next = plan.steps[index].next
            const nextVisit = storyVisits[index + 1]

            if (!next || !nextVisit) return

            counters.followUp += 1

            const createdAt = shiftMinutes(visit.createdAt, 12)

            followUps.push({
                id: `fu_${String(counters.followUp).padStart(4, '0')}`,
                patientId,
                visitId: visit.id,
                dueDate: addDays(visit.date, next.days),
                dueTime: null,
                reason: next.reason,
                notes: '',
                status: 'completed',
                confirmedAt: null,
                completedAt: nextVisit.createdAt,
                cancelledAt: null,
                createdAt,
                updatedAt: nextVisit.createdAt
            })
        })

        const lastVisit = storyVisits[storyVisits.length - 1]
        const lastStep = plan.steps[plan.steps.length - 1]

        if (scenario.followUp) {
            counters.followUp += 1

            const followPlan = scenario.followUp
            const createdAt = shiftMinutes(lastVisit.createdAt, 12)
            const cancelledAt = followPlan.status === 'cancelled' ? stamp(addDays(today, -(followPlan.cancelledAgo ?? 1)), 11, 20) : null
            const confirmation = scenario.reminders?.find(item => item.outcome === 'confirmed')
            const confirmedAt = followPlan.confirmed
                ? stamp(addDays(today, -(confirmation?.daysAgo ?? 0)), 15, 40)
                : null

            followUps.push({
                id: `fu_${String(counters.followUp).padStart(4, '0')}`,
                patientId,
                visitId: lastVisit.id,
                dueDate: addDays(today, followPlan.dueIn),
                dueTime: followPlan.time ?? null,
                reason: lastStep.next?.reason ?? 'Review visit',
                notes: followPlan.note ?? '',
                status: followPlan.status ?? 'pending',
                confirmedAt,
                completedAt: null,
                cancelledAt,
                createdAt,
                updatedAt: cancelledAt ?? confirmedAt ?? createdAt
            })

            const followUp = followUps[followUps.length - 1]
            const reminderSeeds = scenario.reminders ?? []

            reminderSeeds.forEach((item, index) => {
                counters.reminder += 1

                const template = item.channel === 'whatsapp' ? item.template ?? 'reminder' : null
                const message = template && template !== 'custom'
                    ? fillTemplate(settings.followUp.templates[template], {
                        patientName: scenario.name,
                        date: followUp.dueDate,
                        time: followUp.dueTime,
                        treatment: followUp.reason,
                        clinicName: settings.clinic.name,
                        clinicPhone: settings.followUp.contactPhone,
                        doctorName: settings.clinic.doctorName
                    })
                    : ''

                reminders.push({
                    id: `rem_${String(counters.reminder).padStart(4, '0')}`,
                    patientId,
                    followUpId: followUp.id,
                    channel: item.channel,
                    template,
                    message,
                    outcome: item.outcome ?? null,
                    note: item.note ?? '',
                    createdAt: stamp(addDays(today, -item.daysAgo), item.channel === 'call' ? 15 : 10, 10 + index * 7)
                })
            })
        }

        return {
            id: patientId,
            name: scenario.name,
            phone: scenario.phone,
            age: scenario.age,
            ageRecordedOn: plan.firstDate,
            dateOfBirth: null,
            gender: scenario.gender,
            place: scenario.place,
            address: scenario.address ?? '',
            medicalHistory: scenario.medicalHistory ?? '',
            allergies: scenario.allergies ?? '',
            notes: scenario.notes ?? '',
            createdAt: registeredAt,
            updatedAt: registeredAt
        }
    })

    const prescriptions: Prescription[] = prescriptionDrafts
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        .map((draft, index) => ({ ...draft, id: prescriptionCode(1001 + index) }))

    return {
        version: 1,
        patients,
        visits,
        prescriptions,
        followUps,
        reminders,
        treatments: createDefaultTreatments(),
        settings,
        counters: {
            patient: 1000 + patients.length,
            prescription: 1000 + prescriptions.length
        },
        meta: {
            sample: true,
            sampleNoticeDismissed: false,
            createdAt: now.toISOString()
        }
    }
}
